import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import * as Analytics from 'analytics/analytics'
import { useAppDispatch } from 'hooks/reduxHooks'
import { saveUserPreferences } from 'actions/RecommendationActions'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import BodyText from 'components/Luxkit/Typography/BodyText'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import { TRAVEL_PREFERENCES_HOTEL_BRANDS, ModalSteps } from '../defaults'
import TravelPreferencesImageItemsGrid from '../TravelPreferencesImageItemsGrid'

interface Props {
  userPreferences?: App.UserPreferences;
  onSetNextStep: (step: ModalSteps, isSaveAction?: boolean) => void;
}

function TravelPreferencesModalHotelBrands(props: Props) {
  const { userPreferences, onSetNextStep } = props

  const dispatch = useAppDispatch()
  const [selectedHotelBrands, setSelectedHotelBrands] = useState<Set<string>>(new Set(userPreferences?.hotelBrands || []))

  const isSelected = useCallback((name: string) => selectedHotelBrands.has(name), [selectedHotelBrands])

  const onToggleSelection = useCallback((name: string) => {
    setSelectedHotelBrands(prevSelected => {
      const newSelected = new Set(prevSelected)

      if (newSelected.has(name)) {
        newSelected.delete(name)
      } else {
        newSelected.add(name)
      }

      return newSelected
    })
  }, [])

  const onSkip = useCallback(() => {
    onSetNextStep(ModalSteps.travellers)
  }, [onSetNextStep])

  const onUpdatePreferences = useCallback(() => {
    const updatedPreferences = {
      destinations: userPreferences?.destinations || [],
      occupancy: userPreferences?.occupancy || [],
      groupSize: userPreferences?.groupSize,
      holidayTypes: userPreferences?.holidayTypes || [],
      hotelBrands: Array.from(selectedHotelBrands),
    }

    Analytics.trackClientEvent({
      subject: 'hotel_brands',
      action: 'completion',
      type: 'interaction',
      category: 'travel_preferences',
    })

    dispatch(saveUserPreferences(updatedPreferences))
    onSetNextStep(ModalSteps.travellers, true)
  }, [dispatch, onSetNextStep, selectedHotelBrands, userPreferences])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'hotel_brands',
      action: 'impression',
      type: 'nonInteraction',
      category: 'travel_preferences',
    })
  }, [])

  return (
    <>
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={16}>
            <BodyText variant="medium">Enjoy priority access to exclusive offers that suit your preferences. Select up to five brands.</BodyText>
            <TravelPreferencesImageItemsGrid
              showLabel={false}
              items={TRAVEL_PREFERENCES_HOTEL_BRANDS}
              isSelected={isSelected}
              onSelect={onToggleSelection}
            />
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter primaryActionProps={{ children: 'Save & continue', onClick: onUpdatePreferences }} secondaryActionProps={{ children: 'Skip', onClick: onSkip }} />
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  userPreferences: state.recommendations.userPreferences.preferences,
})

export default connect(mapStateToProps)(TravelPreferencesModalHotelBrands)
